import { FC, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import LoaderIcon from '../../assets/icons/loader-icon';
import { AppFormattedMessage } from '../../components/app-formatted-message/index';
import {
  PublicCapTableStatus,
  usePublicCapTable,
} from '../../hooks/shared-cap-table/use-public-cap-table.hook';
import { BrokenLink } from './components/broken-link';
import { PasswordGate } from './components/password-gate';
import { PublicCapTableView } from './components/public-cap-table-view';
import { PublicStatusCard } from './components/public-status-card';

/** Public pages must stay out of search results — the link is the only intended way in. */
const useNoIndex = () => {
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);

    return () => {
      document.head.removeChild(meta);
    };
  }, []);
};

export const SharedCapTablePage: FC = () => {
  const { linkId } = useParams<{ companySlug: string; linkId: string }>();
  const {
    companyLogoUrl,
    companyName,
    exportCapTable,
    exportError,
    isExporting,
    isUnlocking,
    payload,
    status,
    throttledForSeconds,
    unlock,
    unlockFailed,
  } = usePublicCapTable(linkId);
  const [password, setPassword] = useState<string | undefined>();

  useNoIndex();

  if (status === PublicCapTableStatus.LOADING) {
    return (
      <PublicStatusCard>
        <div className="flex flex-col items-center gap-10">
          <LoaderIcon className="size-7" />
          <span className="text-sm font-[550] text-brand-700">
            <AppFormattedMessage id="SHARE_PUBLIC_LOADING" />
          </span>
        </div>
      </PublicStatusCard>
    );
  }

  if (status === PublicCapTableStatus.PASSWORD_REQUIRED) {
    return (
      <PasswordGate
        companyLogoUrl={companyLogoUrl}
        companyName={companyName}
        hasFailed={unlockFailed}
        isSubmitting={isUnlocking}
        onSubmit={(entered) => {
          // Kept in memory for the export call, which must re-present it — never persisted.
          setPassword(entered);
          void unlock(entered);
        }}
        throttledForSeconds={throttledForSeconds}
      />
    );
  }

  // Not a broken link and not a password prompt: the link is fine, its view budget is spent.
  if (status === PublicCapTableStatus.THROTTLED) {
    return (
      <BrokenLink
        descriptionId="SHARE_PUBLIC_THROTTLED_DESCRIPTION"
        titleId="SHARE_PUBLIC_THROTTLED_TITLE"
        values={{ minutes: Math.ceil((throttledForSeconds ?? 60) / 60) }}
      />
    );
  }

  if (status === PublicCapTableStatus.GONE) {
    return (
      <BrokenLink
        descriptionId="SHARE_PUBLIC_BROKEN_DESCRIPTION"
        titleId="SHARE_PUBLIC_BROKEN_TITLE"
      />
    );
  }

  if (status !== PublicCapTableStatus.READY || !payload) {
    return (
      <BrokenLink
        descriptionId="SHARE_PUBLIC_ERROR_DESCRIPTION"
        titleId="SHARE_PUBLIC_ERROR_TITLE"
      />
    );
  }

  return (
    <PublicCapTableView
      exportError={exportError}
      isExporting={isExporting}
      onExport={() => void exportCapTable(password)}
      payload={payload}
    />
  );
};
